<template>
    <div class="install-page">
        <div class="install-shell">
            <section
                v-if="handoffState !== 'absent' && handoffState !== 'skipped'"
                class="install-handoff"
                aria-live="polite"
            >
                <template v-if="handoffState === 'checking'">
                    <span class="install-handoff__icon">
                        <span class="install-state-spinner" aria-hidden="true"></span>
                    </span>
                    <div>
                        <strong>Проверяем безопасную ссылку для входа</strong>
                        <span>Это займёт несколько секунд.</span>
                    </div>
                </template>
                <template v-else-if="handoffState === 'ready'">
                    <span class="install-handoff__icon">
                        <i class="fas fa-user-check" aria-hidden="true"></i>
                    </span>
                    <div class="install-handoff__copy">
                        <strong>Установить и войти в свой аккаунт</strong>
                        <span>
                            После подтверждения Аргус сохранит вход для запуска с ярлыка.
                        </span>
                    </div>
                    <div class="install-handoff__actions">
                        <button
                            type="button"
                            class="install-handoff__button install-handoff__button--primary"
                            :disabled="handoffInProgress"
                            @click="exchangeHandoff"
                        >
                            {{ handoffInProgress
                                ? 'Выполняем вход…'
                                : `Войти как ${handoffDisplayName}` }}
                        </button>
                        <button
                            type="button"
                            class="install-handoff__button"
                            :disabled="handoffInProgress"
                            @click="skipHandoff"
                        >
                            Установить без входа
                        </button>
                    </div>
                </template>
                <template v-else-if="handoffState === 'authenticated'">
                    <span class="install-handoff__icon install-handoff__icon--success">
                        <i class="fas fa-check" aria-hidden="true"></i>
                    </span>
                    <div>
                        <strong>Вход выполнен</strong>
                        <span>
                            Вы вошли как {{ handoffDisplayName }}. Теперь установите Аргус.
                        </span>
                    </div>
                </template>
                <template v-else-if="handoffState === 'unavailable'">
                    <span class="install-handoff__icon install-handoff__icon--warning">
                        <i class="fas fa-wifi" aria-hidden="true"></i>
                    </span>
                    <div class="install-handoff__copy">
                        <strong>
                            {{ handoffRetryMode === 'exchange'
                                ? 'Не удалось завершить вход'
                                : 'Не удалось проверить ссылку для входа' }}
                        </strong>
                        <span>
                            Ссылка сохранена. Проверьте соединение и попробуйте ещё раз.
                        </span>
                    </div>
                    <div class="install-handoff__actions">
                        <button
                            type="button"
                            class="install-handoff__button install-handoff__button--primary"
                            :disabled="handoffInProgress"
                            @click="retryHandoff"
                        >
                            {{ handoffRetryMode === 'exchange'
                                ? 'Повторить вход'
                                : 'Проверить ещё раз' }}
                        </button>
                        <button
                            v-if="handoffRetryMode !== 'exchange'"
                            type="button"
                            class="install-handoff__button"
                            :disabled="handoffInProgress"
                            @click="skipHandoff"
                        >
                            Установить без входа
                        </button>
                    </div>
                </template>
                <template v-else>
                    <span class="install-handoff__icon install-handoff__icon--warning">
                        <i class="fas fa-clock" aria-hidden="true"></i>
                    </span>
                    <div>
                        <strong>Ссылка для входа истекла</strong>
                        <span>
                            Установить Аргус всё равно можно. Войти получится при первом запуске.
                        </span>
                    </div>
                </template>
            </section>

            <section class="install-hero">
                <div class="install-brand">
                    <img
                        src="/icons/icon-512.png"
                        alt="Логотип Аргус"
                        class="install-brand__logo"
                    >
                    <div>
                        <div class="install-brand__eyebrow">Веб-приложение</div>
                        <div class="install-brand__name">Аргус</div>
                    </div>
                </div>

                <h1>Установите Аргус</h1>
                <p class="install-hero__lead">
                    Открывайте рабочую систему одним нажатием — на смартфоне или компьютере.
                </p>

                <div class="install-benefits" aria-label="Преимущества установки">
                    <div class="install-benefit">
                        <span class="install-benefit__icon">
                            <i class="fas fa-rocket"></i>
                        </span>
                        <div>
                            <strong>Быстрый запуск</strong>
                            <span>Иконка всегда под рукой</span>
                        </div>
                    </div>
                    <div class="install-benefit">
                        <span class="install-benefit__icon">
                            <i class="fas fa-window-maximize"></i>
                        </span>
                        <div>
                            <strong>Отдельное окно</strong>
                            <span>Без лишних панелей браузера</span>
                        </div>
                    </div>
                    <div class="install-benefit">
                        <span class="install-benefit__icon">
                            <i class="fas fa-sync-alt"></i>
                        </span>
                        <div>
                            <strong>Всегда актуально</strong>
                            <span>Обновления приходят автоматически</span>
                        </div>
                    </div>
                </div>
            </section>

            <section
                class="install-card"
                :class="{ 'install-card--ready': deferredInstallPrompt && !isEmbeddedBrowser && !isStandalone }"
                aria-live="polite"
            >
                <template v-if="isIOSTelegramBrowser">
                    <div class="install-state-icon install-state-icon--telegram">
                        <i class="fab fa-telegram-plane"></i>
                    </div>
                    <h2>Сначала откройте страницу в Safari</h2>
                    <p>
                        Во встроенном браузере Telegram нет команды
                        <strong>«На экран “Домой”»</strong>.
                    </p>
                    <button
                        type="button"
                        class="install-telegram-safari-preview"
                        aria-label="Открыть подсказку перехода из Telegram в Safari"
                        @click="openIOSTelegramPreview"
                    >
                        <img
                            src="/install-previews/ios-telegram-open-safari.png"
                            alt="Обведённый значок Safari в правом нижнем углу браузера Telegram"
                        >
                        <span class="install-telegram-safari-preview__zoom" aria-hidden="true">
                            <i class="fas fa-expand-alt"></i>
                            Открыть
                        </span>
                    </button>
                    <ol class="install-steps install-steps--telegram-ios">
                        <li>
                            Нажмите обведённый значок <strong>Safari</strong>
                            в правом нижнем углу
                        </li>
                        <li>
                            В Safari нажмите <strong>«Поделиться»</strong>
                            <i class="fas fa-share-square install-inline-icon"></i>
                        </li>
                        <li>Выберите <strong>«На экран “Домой”»</strong></li>
                        <li>Нажмите <strong>«Добавить»</strong></li>
                    </ol>
                    <button
                        type="button"
                        class="install-button install-button--secondary"
                        @click="copyInstallLink"
                    >
                        <i class="far fa-copy"></i>
                        Скопировать ссылку
                    </button>
                    <p v-if="copyMessage" class="install-feedback">{{ copyMessage }}</p>
                </template>

                <template v-else-if="isEmbeddedBrowser">
                    <div class="install-state-icon install-state-icon--warning">
                        <i class="fas fa-external-link-alt"></i>
                    </div>
                    <h2>Откройте ссылку в браузере</h2>
                    <p>
                        Встроенный браузер мессенджера может не поддерживать установку.
                        Откройте эту страницу в Chrome, Edge или Safari.
                    </p>
                    <button
                        type="button"
                        class="install-button install-button--primary"
                        @click="openExternalBrowser"
                    >
                        <i class="fas fa-external-link-alt"></i>
                        Открыть во внешнем браузере
                    </button>
                    <button
                        type="button"
                        class="install-button install-button--secondary"
                        @click="copyInstallLink"
                    >
                        <i class="far fa-copy"></i>
                        Скопировать ссылку
                    </button>
                    <p v-if="copyMessage" class="install-feedback">{{ copyMessage }}</p>
                    <p v-if="externalOpenAttempted" class="install-hint">
                        Если браузер не открылся, скопируйте ссылку и вставьте её в адресную строку.
                    </p>
                </template>

                <template v-else-if="isStandalone || installationConfirmed">
                    <div class="install-state-icon install-state-icon--success">
                        <i class="fas fa-check"></i>
                    </div>
                    <h2>Аргус уже установлен</h2>
                    <p v-if="isStandalone">
                        Приложение запущено в отдельном окне и готово к работе.
                    </p>
                    <p v-else>
                        Больше не будем предлагать установку на этом устройстве.
                    </p>
                    <button
                        type="button"
                        class="install-button install-button--primary"
                        @click="openArgus"
                    >
                        Открыть Аргус
                    </button>
                </template>

                <template v-else-if="isIOS">
                    <div class="install-state-icon">
                        <i class="fab fa-apple"></i>
                    </div>
                    <h2>Установка на iPhone или iPad</h2>
                    <ol class="install-steps">
                        <li>
                            Нажмите <strong>«Поделиться»</strong>
                            <i class="fas fa-share-square install-inline-icon"></i>
                        </li>
                        <li>Выберите <strong>«На экран “Домой”»</strong></li>
                        <li>Нажмите <strong>«Добавить»</strong></li>
                    </ol>
                    <button
                        type="button"
                        class="install-button install-button--secondary"
                        @click="openArgus"
                    >
                        Открыть без установки
                    </button>
                    <button
                        type="button"
                        class="install-button install-button--secondary"
                        @click="confirmAlreadyInstalled"
                    >
                        Уже установлено на этом устройстве
                    </button>
                </template>

                <template v-else-if="isMacSafari">
                    <div class="install-state-icon">
                        <i class="fab fa-safari"></i>
                    </div>
                    <h2>Установка в Safari</h2>
                    <ol class="install-steps">
                        <li>Нажмите кнопку <strong>«Поделиться»</strong> в Safari</li>
                        <li>Выберите <strong>«Добавить в Dock»</strong></li>
                        <li>Подтвердите добавление приложения</li>
                    </ol>
                    <button
                        type="button"
                        class="install-button install-button--secondary"
                        @click="openArgus"
                    >
                        Открыть без установки
                    </button>
                </template>

                <template v-else-if="deferredInstallPrompt">
                    <div class="install-state-icon">
                        <i class="fas fa-download"></i>
                    </div>
                    <div class="install-ready-badge">
                        <i class="fas fa-check-circle"></i>
                        Доступно для установки
                    </div>
                    <h2>Аргус готов к установке</h2>
                    <p>
                        Браузер добавит приложение на устройство и создаст отдельное окно.
                    </p>
                    <button
                        type="button"
                        class="install-button install-button--primary"
                        :disabled="installInProgress"
                        @click="requestInstall"
                    >
                        <span
                            v-if="installInProgress"
                            class="install-button__spinner"
                            aria-hidden="true"
                        ></span>
                        <i v-else class="fas fa-download"></i>
                        {{ installInProgress ? 'Открываем установку…' : installButtonLabel }}
                    </button>
                    <button
                        type="button"
                        class="install-button install-button--secondary"
                        @click="openArgus"
                    >
                        Открыть без установки
                    </button>
                </template>

                <template v-else-if="!promptCheckComplete">
                    <div class="install-state-icon">
                        <span class="install-state-spinner" aria-hidden="true"></span>
                    </div>
                    <h2>Проверяем возможность установки</h2>
                    <p>Это займёт всего несколько секунд.</p>
                </template>

                <template v-else-if="isAndroid">
                    <div class="install-state-icon">
                        <i class="fab fa-android"></i>
                    </div>
                    <h2>Установите через меню браузера</h2>
                    <ol class="install-steps">
                        <li>Откройте меню браузера <strong>⋮</strong></li>
                        <li>
                            Выберите <strong>«Установить приложение»</strong>
                            или <strong>«Добавить на главный экран»</strong>
                        </li>
                        <li>Подтвердите установку</li>
                    </ol>
                    <p v-if="installDismissed" class="install-feedback">
                        Системное окно установки было закрыто. Его можно открыть повторно через меню браузера.
                    </p>
                    <button
                        type="button"
                        class="install-button install-button--secondary"
                        @click="openArgus"
                    >
                        Открыть без установки
                    </button>
                </template>

                <template v-else-if="isChromium">
                    <div class="install-state-icon">
                        <i class="fas fa-desktop"></i>
                    </div>
                    <h2>Установите Аргус на компьютер</h2>
                    <p>
                        Используйте значок установки в адресной строке или пункт
                        <strong>«Установить Аргус»</strong> в меню браузера.
                    </p>
                    <p v-if="installDismissed" class="install-feedback">
                        Системное окно было закрыто. Установку можно повторить через меню браузера.
                    </p>
                    <button
                        type="button"
                        class="install-button install-button--secondary"
                        @click="openArgus"
                    >
                        Открыть Аргус
                    </button>
                </template>

                <template v-else>
                    <div class="install-state-icon">
                        <i class="fas fa-globe"></i>
                    </div>
                    <h2>Откройте Аргус в браузере</h2>
                    <p>
                        Этот браузер не предоставил установку. Для установки используйте
                        Chrome, Edge или Safari.
                    </p>
                    <button
                        type="button"
                        class="install-button install-button--primary"
                        @click="openArgus"
                    >
                        Открыть Аргус
                    </button>
                </template>
            </section>
        </div>

        <section class="install-preview-section">
            <div class="install-preview-section__head">
                <span>ПОСЛЕ УСТАНОВКИ</span>
                <h2>Аргус появится прямо на вашем устройстве</h2>
                <p>
                    Запускайте его с экрана телефона, рабочего стола или панели задач —
                    без поисков ссылки в Telegram и браузере.
                </p>
            </div>
            <InstallAppPreview size="full"/>
        </section>

        <footer class="install-footer">
            Установка не требует App Store или Google Play
        </footer>
    </div>
</template>

<script>
import InstallAppPreview from '@/components/InstallAppPreview.vue';
import {
    exchangeInstallHandoff,
    inspectInstallHandoff
} from '@/utils/appInstallCampaignApi';
import {
    clearCapturedInstallPrompt,
    createInstallRequestId,
    getBrowserInstallDeviceId,
    getCapturedInstallPrompt,
    getInstallCtaLabel,
    getInstallPlatform,
    getInstallUrl,
    hasRememberedAppInstallation,
    INSTALLATION_STATE_CHANGED_EVENT,
    isInstalledDisplayMode,
    markAppAlreadyInstalled,
    markAppInstalled,
    openInstallUrlExternally
} from '@/utils/appInstallation';

const PROMPT_CHECK_DELAY = 1500;
const INSTALL_CONTROLLED_REFRESH_KEY = 'argus.install.controlledRefresh.v1';
const INSTALL_HANDOFF_SESSION_KEY = 'argus.install.handoff.v1';
const INSTALL_HANDOFF_REQUEST_SESSION_KEY =
    'argus.install.handoffRequestId.v1';
const INSTALL_HANDOFF_EXCHANGE_STARTED_SESSION_KEY =
    'argus.install.handoffExchangeStarted.v1';
const INSTALL_HANDOFF_DISPLAY_NAME_SESSION_KEY =
    'argus.install.handoffDisplayName.v1';

function isTerminalHandoffError(error) {
    const status = error && error.response && error.response.status;
    const code = error
        && error.response
        && error.response.data
        && error.response.data.code;
    return status === 410 || code === 'HANDOFF_INVALID';
}

export default {
    name: 'InstallApp',
    components: {
        InstallAppPreview
    },
    data() {
        return {
            deferredInstallPrompt: null,
            installInProgress: false,
            installDismissed: false,
            installationConfirmed: false,
            promptCheckComplete: false,
            promptCheckTimer: null,
            controlledRefreshTimer: null,
            serviceWorkerControllerHandler: null,
            copyMessage: '',
            externalOpenAttempted: false,
            handoffCode: '',
            handoffRequestId: '',
            handoffDisplayName: '',
            handoffState: 'absent',
            handoffInProgress: false,
            handoffRetryMode: 'inspect',
            environment: {
                isAndroid: false,
                isIOS: false,
                isMacOS: false,
                isSafari: false,
                isChromium: false,
                isIOSTelegramBrowser: false,
                isEmbeddedBrowser: false,
                isStandalone: false
            }
        };
    },
    computed: {
        isAndroid() {
            return this.environment.isAndroid;
        },
        isIOS() {
            return this.environment.isIOS;
        },
        isMacSafari() {
            return this.environment.isMacOS && this.environment.isSafari;
        },
        isChromium() {
            return this.environment.isChromium;
        },
        isIOSTelegramBrowser() {
            return this.environment.isIOSTelegramBrowser;
        },
        isEmbeddedBrowser() {
            return this.environment.isEmbeddedBrowser;
        },
        isStandalone() {
            return this.environment.isStandalone;
        },
        installUrl() {
            return getInstallUrl(this.handoffCode);
        },
        installButtonLabel() {
            return getInstallCtaLabel();
        }
    },
    mounted() {
        this.initializeHandoff();
        this.detectEnvironment();
        this.installationConfirmed = hasRememberedAppInstallation();
        window.addEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
        window.addEventListener(
            INSTALLATION_STATE_CHANGED_EVENT,
            this.onInstallationStateChanged
        );
        this.restoreCapturedInstallPrompt();
        this.prepareFirstInstallVisit();
        this.promptCheckTimer = window.setTimeout(() => {
            this.promptCheckComplete = true;
        }, PROMPT_CHECK_DELAY);
    },
    beforeDestroy() {
        window.removeEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
        window.removeEventListener(
            INSTALLATION_STATE_CHANGED_EVENT,
            this.onInstallationStateChanged
        );
        if (this.promptCheckTimer) {
            window.clearTimeout(this.promptCheckTimer);
        }
        if (this.controlledRefreshTimer) {
            window.clearTimeout(this.controlledRefreshTimer);
        }
        if (this.serviceWorkerControllerHandler && 'serviceWorker' in navigator) {
            navigator.serviceWorker.removeEventListener(
                'controllerchange',
                this.serviceWorkerControllerHandler
            );
        }
    },
    methods: {
        readHandoffFromLocation() {
            const rawHash = window.location.hash
                ? window.location.hash.slice(1)
                : '';
            const code = new URLSearchParams(rawHash).get('handoff') || '';
            if (code) {
                const requestId = createInstallRequestId();
                try {
                    window.sessionStorage.setItem(INSTALL_HANDOFF_SESSION_KEY, code);
                    window.sessionStorage.setItem(
                        INSTALL_HANDOFF_REQUEST_SESSION_KEY,
                        requestId
                    );
                    window.sessionStorage.removeItem(
                        INSTALL_HANDOFF_EXCHANGE_STARTED_SESSION_KEY
                    );
                    window.sessionStorage.removeItem(
                        INSTALL_HANDOFF_DISPLAY_NAME_SESSION_KEY
                    );
                } catch (error) {
                    // The in-memory copy below is enough for the current page.
                }
                this.handoffRequestId = requestId;
                this.handoffRetryMode = 'inspect';
                window.history.replaceState(
                    window.history.state,
                    document.title,
                    `${window.location.pathname}${window.location.search}`
                );
                return code;
            }
            try {
                const storedCode =
                    window.sessionStorage.getItem(INSTALL_HANDOFF_SESSION_KEY) || '';
                if (!storedCode) return '';
                let requestId = window.sessionStorage.getItem(
                    INSTALL_HANDOFF_REQUEST_SESSION_KEY
                ) || '';
                if (!requestId) {
                    requestId = createInstallRequestId();
                    window.sessionStorage.setItem(
                        INSTALL_HANDOFF_REQUEST_SESSION_KEY,
                        requestId
                    );
                }
                this.handoffRequestId = requestId;
                this.handoffDisplayName = window.sessionStorage.getItem(
                    INSTALL_HANDOFF_DISPLAY_NAME_SESSION_KEY
                ) || '';
                this.handoffRetryMode = window.sessionStorage.getItem(
                    INSTALL_HANDOFF_EXCHANGE_STARTED_SESSION_KEY
                ) === '1'
                    ? 'exchange'
                    : 'inspect';
                return storedCode;
            } catch (error) {
                return '';
            }
        },
        clearStoredHandoff() {
            this.handoffCode = '';
            this.handoffRequestId = '';
            try {
                window.sessionStorage.removeItem(INSTALL_HANDOFF_SESSION_KEY);
                window.sessionStorage.removeItem(
                    INSTALL_HANDOFF_REQUEST_SESSION_KEY
                );
                window.sessionStorage.removeItem(
                    INSTALL_HANDOFF_EXCHANGE_STARTED_SESSION_KEY
                );
                window.sessionStorage.removeItem(
                    INSTALL_HANDOFF_DISPLAY_NAME_SESSION_KEY
                );
            } catch (error) {
                // Nothing sensitive remains in the URL.
            }
        },
        async initializeHandoff() {
            const code = this.readHandoffFromLocation();
            if (!code) {
                this.handoffState = 'absent';
                return;
            }
            this.handoffCode = code;
            if (this.handoffRetryMode === 'exchange') {
                this.handoffState = 'unavailable';
                return;
            }
            this.handoffState = 'checking';
            try {
                const result = await inspectInstallHandoff(code);
                if (!result || result.valid !== true || !result.displayName) {
                    this.handoffState = 'expired';
                    this.clearStoredHandoff();
                    return;
                }
                this.handoffDisplayName = result.displayName;
                try {
                    window.sessionStorage.setItem(
                        INSTALL_HANDOFF_DISPLAY_NAME_SESSION_KEY,
                        result.displayName
                    );
                } catch (error) {
                    // The name is only a convenience for retry copy.
                }
                this.handoffRetryMode = 'inspect';
                this.handoffState = 'ready';
            } catch (error) {
                if (isTerminalHandoffError(error)) {
                    this.handoffState = 'expired';
                    this.clearStoredHandoff();
                } else {
                    this.handoffState = 'unavailable';
                }
            }
        },
        async exchangeHandoff() {
            if (
                !this.handoffCode
                || (
                    this.handoffState !== 'ready'
                    && !(
                        this.handoffState === 'unavailable'
                        && this.handoffRetryMode === 'exchange'
                    )
                )
                || this.handoffInProgress
            ) {
                return;
            }
            this.handoffInProgress = true;
            this.handoffRetryMode = 'exchange';
            try {
                window.sessionStorage.setItem(
                    INSTALL_HANDOFF_EXCHANGE_STARTED_SESSION_KEY,
                    '1'
                );
            } catch (error) {
                // In-memory retry still works until the page is reloaded.
            }
            try {
                const result = await exchangeInstallHandoff({
                    code: this.handoffCode,
                    requestId: this.handoffRequestId,
                    browserDeviceId: getBrowserInstallDeviceId(),
                    platform: getInstallPlatform()
                });
                if (!result || !result.accessToken || !result.refreshToken) {
                    throw new Error('Install handoff did not return a session');
                }
                this.$store.commit('setToken', result.accessToken);
                this.$store.commit('setRefreshToken', result.refreshToken);
                this.$store.dispatch('setAuthorizationHeader', result.accessToken);
                this.$store.dispatch('updateAbility', this);
                this.$store.dispatch('navigation_loadSettings', {silent: true});
                if (isInstalledDisplayMode()) {
                    await markAppInstalled('standalone');
                } else if (hasRememberedAppInstallation()) {
                    await markAppInstalled('appinstalled');
                }
                this.handoffState = 'authenticated';
                this.clearStoredHandoff();
            } catch (error) {
                if (isTerminalHandoffError(error)) {
                    this.handoffState = 'expired';
                    this.clearStoredHandoff();
                } else {
                    this.handoffRetryMode = 'exchange';
                    this.handoffState = 'unavailable';
                }
            } finally {
                this.handoffInProgress = false;
            }
        },
        retryHandoff() {
            if (this.handoffRetryMode === 'exchange') {
                return this.exchangeHandoff();
            }
            return this.initializeHandoff();
        },
        skipHandoff() {
            this.clearStoredHandoff();
            this.handoffState = 'skipped';
        },
        restoreCapturedInstallPrompt() {
            const prompt = getCapturedInstallPrompt();
            if (!prompt) {
                return;
            }
            this.deferredInstallPrompt = prompt;
            this.promptCheckComplete = true;
            this.installDismissed = false;
        },
        detectEnvironment() {
            const userAgent = navigator.userAgent || '';
            const isIPadDesktopMode =
                navigator.platform === 'MacIntel'
                && navigator.maxTouchPoints > 1;
            const isIOS =
                /iPad|iPhone|iPod/i.test(userAgent)
                || isIPadDesktopMode;
            const isAndroid = /Android/i.test(userAgent);
            const isMacOS =
                !isIOS
                && /Macintosh|Mac OS X/i.test(userAgent);
            const isSafari =
                /Safari/i.test(userAgent)
                && !/Chrome|Chromium|CriOS|FxiOS|EdgiOS|OPR|Edg/i.test(userAgent);
            const isChromium =
                /Chrome|Chromium|CriOS|Edg|OPR/i.test(userAgent);
            const isIOSWebView =
                isIOS
                && /AppleWebKit/i.test(userAgent)
                && !/Safari/i.test(userAgent);
            const installSource =
                new URLSearchParams(window.location.search).get('source') || '';
            const hasTelegramWebApp = Boolean(
                window.Telegram
                && window.Telegram.WebApp
                && window.Telegram.WebApp.initData
            );
            const hasTelegramWebviewProxy =
                typeof window.TelegramWebviewProxy !== 'undefined';
            const isIOSTelegramBrowser =
                isIOS
                && (
                    /Telegram/i.test(userAgent)
                    || hasTelegramWebApp
                    || hasTelegramWebviewProxy
                    || (installSource === 'telegram' && isIOSWebView)
                );
            const isEmbeddedBrowser =
                isIOSTelegramBrowser
                || isIOSWebView
                || /FBAN|FBAV|Instagram|Line\/|Telegram|;\s*wv\)|\bwv\b/i.test(userAgent);
            const isStandalone =
                (
                    window.matchMedia
                    && window.matchMedia('(display-mode: standalone)').matches
                )
                || navigator.standalone === true
                || document.referrer.indexOf('android-app://') === 0;

            this.environment = {
                isAndroid,
                isIOS,
                isMacOS,
                isSafari,
                isChromium,
                isIOSTelegramBrowser,
                isEmbeddedBrowser,
                isStandalone
            };
        },
        prepareFirstInstallVisit() {
            if (
                !this.isChromium
                || this.isEmbeddedBrowser
                || !('serviceWorker' in navigator)
                || navigator.serviceWorker.controller
            ) {
                return;
            }
            try {
                if (sessionStorage.getItem(INSTALL_CONTROLLED_REFRESH_KEY) === 'done') {
                    return;
                }
            } catch (error) {
                return;
            }

            this.serviceWorkerControllerHandler = () => {
                navigator.serviceWorker.removeEventListener(
                    'controllerchange',
                    this.serviceWorkerControllerHandler
                );
                this.serviceWorkerControllerHandler = null;
                if (this.controlledRefreshTimer) {
                    window.clearTimeout(this.controlledRefreshTimer);
                }
                this.controlledRefreshTimer = window.setTimeout(() => {
                    if (
                        this.deferredInstallPrompt
                        || getCapturedInstallPrompt()
                    ) {
                        return;
                    }
                    try {
                        sessionStorage.setItem(INSTALL_CONTROLLED_REFRESH_KEY, 'done');
                    } catch (error) {
                        return;
                    }
                    window.location.reload();
                }, 300);
            };
            navigator.serviceWorker.addEventListener(
                'controllerchange',
                this.serviceWorkerControllerHandler,
                {once: true}
            );
            navigator.serviceWorker.ready.then(() => {
                if (
                    navigator.serviceWorker.controller
                    && this.serviceWorkerControllerHandler
                ) {
                    this.serviceWorkerControllerHandler();
                }
            }).catch(() => {});
        },
        onBeforeInstallPrompt(event) {
            event.preventDefault();
            this.deferredInstallPrompt = event;
            this.promptCheckComplete = true;
            this.installDismissed = false;
            if (this.promptCheckTimer) {
                window.clearTimeout(this.promptCheckTimer);
                this.promptCheckTimer = null;
            }
        },
        onInstallationStateChanged() {
            this.installationConfirmed = hasRememberedAppInstallation();
            if (!this.installationConfirmed) return;
            this.deferredInstallPrompt = null;
            this.installInProgress = false;
        },
        confirmAlreadyInstalled() {
            markAppAlreadyInstalled();
            this.installationConfirmed = true;
        },
        async requestInstall() {
            if (!this.deferredInstallPrompt || this.installInProgress) {
                return;
            }

            const prompt = this.deferredInstallPrompt;
            this.installInProgress = true;

            try {
                await prompt.prompt();
                const choice = await prompt.userChoice;
                this.installDismissed = !choice || choice.outcome !== 'accepted';
            } finally {
                clearCapturedInstallPrompt();
                this.deferredInstallPrompt = null;
                this.installInProgress = false;
                this.promptCheckComplete = true;
            }
        },
        openArgus() {
            this.$router.push('/');
        },
        openIOSTelegramPreview() {
            const file = {
                id: 'ios-telegram-open-safari',
                name: 'Как открыть Аргус из Telegram в Safari.png',
                url: '/install-previews/ios-telegram-open-safari.png',
                downloadUrl: '/install-previews/ios-telegram-open-safari.png',
                mimeType: 'image/png',
                isImage: true
            };
            if (this.$fileViewer) {
                this.$fileViewer.open([file], {
                    startId: file.id,
                    source: 'install-instructions'
                });
                return;
            }
            window.open(file.url, '_blank', 'noopener,noreferrer');
        },
        async openExternalBrowser() {
            this.externalOpenAttempted = true;
            await openInstallUrlExternally(
                this.handoffCode,
                {allowPlain: true}
            );
        },
        async copyInstallLink() {
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(this.installUrl);
                } else {
                    this.copyLinkWithFallback();
                }
                this.copyMessage = 'Ссылка скопирована';
            } catch (error) {
                try {
                    this.copyLinkWithFallback();
                    this.copyMessage = 'Ссылка скопирована';
                } catch (fallbackError) {
                    this.copyMessage = `Скопируйте ссылку: ${this.installUrl}`;
                }
            }
        },
        copyLinkWithFallback() {
            const textArea = document.createElement('textarea');
            textArea.value = this.installUrl;
            textArea.setAttribute('readonly', '');
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            const copied = document.execCommand('copy');
            document.body.removeChild(textArea);
            if (!copied) {
                throw new Error('Copy command is unavailable');
            }
        }
    }
};
</script>

<style scoped>
.install-page {
    min-height: 100vh;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    color: #1f2933;
    background:
        radial-gradient(circle at 8% 10%, rgba(0, 123, 255, .12), transparent 34rem),
        radial-gradient(circle at 92% 90%, rgba(52, 58, 64, .11), transparent 32rem),
        #f4f7fa;
}

.install-shell {
    width: 100%;
    max-width: 1040px;
    margin: auto;
    padding: 40px 24px 24px;
    display: grid;
    grid-template-columns: minmax(0, 1.05fr) minmax(340px, .95fr);
    column-gap: 56px;
    row-gap: 28px;
    align-items: center;
}

.install-handoff {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: 48px minmax(0, 1fr) auto;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    border: 1px solid #b8d3fb;
    border-radius: 17px;
    background: rgba(255, 255, 255, .92);
    box-shadow: 0 10px 30px rgba(37, 86, 148, .10);
}

.install-handoff__icon {
    display: inline-flex;
    width: 48px;
    height: 48px;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    color: #fff;
    background: linear-gradient(145deg, #0d6efd, #3d8bfd);
    font-size: 19px;
}

.install-handoff__icon--success {
    background: linear-gradient(145deg, #16833d, #28a745);
}

.install-handoff__icon--warning {
    background: linear-gradient(145deg, #d97706, #f59e0b);
}

.install-handoff strong,
.install-handoff span {
    display: block;
}

.install-handoff strong {
    margin-bottom: 3px;
    color: #173b6a;
    font-size: 15px;
}

.install-handoff div > span {
    color: #66778b;
    font-size: 13px;
    line-height: 1.4;
}

.install-handoff__copy {
    min-width: 0;
}

.install-handoff__actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.install-handoff__button {
    min-height: 39px;
    padding: 8px 13px;
    border: 1px solid #cad7e6;
    border-radius: 11px;
    color: #34475d;
    background: #fff;
    font: inherit;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
}

.install-handoff__button--primary {
    border-color: #0d6efd;
    color: #fff;
    background: #0d6efd;
}

.install-handoff__button:disabled {
    cursor: wait;
    opacity: .68;
}

.install-handoff__button:focus-visible {
    outline: 3px solid rgba(13, 110, 253, .22);
    outline-offset: 2px;
}

.install-brand {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 32px;
}

.install-brand__logo {
    display: block;
    width: 86px;
    height: 86px;
    object-fit: contain;
    border-radius: 24px;
    background: #fff;
    box-shadow: 0 14px 34px rgba(31, 41, 51, .16);
}

.install-brand__eyebrow {
    margin-bottom: 2px;
    color: #637381;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: .08em;
    text-transform: uppercase;
}

.install-brand__name {
    color: #20262d;
    font-size: 30px;
    font-weight: 700;
    line-height: 1.1;
}

.install-hero h1 {
    margin: 0;
    color: #20262d;
    font-size: clamp(38px, 5vw, 58px);
    font-weight: 750;
    line-height: 1.05;
    letter-spacing: -.035em;
}

.install-hero__lead {
    max-width: 560px;
    margin: 20px 0 32px;
    color: #52616b;
    font-size: 20px;
    line-height: 1.5;
}

.install-benefits {
    display: grid;
    gap: 15px;
}

.install-benefit {
    display: flex;
    align-items: center;
    gap: 14px;
}

.install-benefit__icon {
    flex: 0 0 42px;
    width: 42px;
    height: 42px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    color: #fff;
    background: #343a40;
    box-shadow: 0 8px 18px rgba(52, 58, 64, .18);
}

.install-benefit strong,
.install-benefit div > span {
    display: block;
}

.install-benefit strong {
    color: #2f3a43;
    font-size: 15px;
}

.install-benefit div > span {
    margin-top: 2px;
    color: #6b7882;
    font-size: 14px;
}

.install-card {
    min-height: 430px;
    padding: 38px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    border: 1px solid rgba(120, 135, 150, .2);
    border-radius: 28px;
    background: rgba(255, 255, 255, .94);
    box-shadow: 0 24px 70px rgba(31, 41, 51, .16);
    backdrop-filter: blur(12px);
    transform: translateY(18px);
}

.install-card h2 {
    margin: 4px 0 12px;
    color: #25313a;
    font-size: 26px;
    font-weight: 700;
    line-height: 1.25;
    text-align: center;
}

.install-card > p {
    margin: 0 0 22px;
    color: #5f6c75;
    font-size: 15px;
    line-height: 1.55;
    text-align: center;
}

.install-card--ready {
    border: 2px solid #0d6efd;
    background:
        linear-gradient(160deg, rgba(232, 242, 255, .98), rgba(255, 255, 255, .98) 58%);
    box-shadow:
        0 26px 74px rgba(13, 110, 253, .22),
        0 0 0 7px rgba(13, 110, 253, .08);
}

.install-card--ready .install-state-icon {
    background: linear-gradient(145deg, #0d6efd, #3d8bfd);
    box-shadow: 0 13px 28px rgba(13, 110, 253, .32);
}

.install-card--ready h2 {
    color: #084298;
}

.install-card--ready .install-button--primary {
    background: #0d6efd;
    box-shadow: 0 12px 25px rgba(13, 110, 253, .3);
}

.install-card--ready .install-button--primary:hover:not(:disabled) {
    background: #0b5ed7;
    box-shadow: 0 15px 30px rgba(13, 110, 253, .36);
}

.install-ready-badge {
    align-self: center;
    margin: 0 0 10px;
    padding: 7px 12px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid rgba(13, 110, 253, .2);
    border-radius: 999px;
    color: #084298;
    background: rgba(207, 226, 255, .72);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: .02em;
    text-transform: uppercase;
}

.install-state-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 21px;
    color: #fff;
    background: linear-gradient(145deg, #343a40, #59636d);
    box-shadow: 0 12px 26px rgba(52, 58, 64, .24);
    font-size: 27px;
}

.install-state-icon--success {
    background: linear-gradient(145deg, #16833d, #28a745);
    box-shadow: 0 12px 26px rgba(40, 167, 69, .24);
}

.install-state-icon--telegram {
    background: linear-gradient(145deg, #229ed9, #168acd);
    box-shadow: 0 12px 26px rgba(34, 158, 217, .26);
}

.install-state-icon--warning {
    background: linear-gradient(145deg, #d97706, #f59e0b);
    box-shadow: 0 12px 26px rgba(217, 119, 6, .24);
}

.install-button {
    width: 100%;
    min-height: 50px;
    margin-top: 11px;
    padding: 12px 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    border: 1px solid transparent;
    border-radius: 13px;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.2;
    cursor: pointer;
    transition: transform .15s ease, box-shadow .15s ease, background-color .15s ease;
}

.install-button:hover:not(:disabled) {
    transform: translateY(-1px);
}

.install-button:focus-visible {
    outline: 3px solid rgba(0, 123, 255, .3);
    outline-offset: 2px;
}

.install-button:disabled {
    cursor: wait;
    opacity: .72;
}

.install-button--primary {
    color: #fff;
    background: #343a40;
    box-shadow: 0 10px 22px rgba(52, 58, 64, .2);
}

.install-button--primary:hover:not(:disabled) {
    color: #fff;
    background: #252a2e;
    box-shadow: 0 13px 26px rgba(52, 58, 64, .27);
}

.install-button--secondary {
    color: #36414a;
    border-color: #d8dee3;
    background: #fff;
}

.install-button--secondary:hover:not(:disabled) {
    color: #20262d;
    border-color: #b9c3cb;
    background: #f8fafb;
}

.install-button__spinner,
.install-state-spinner {
    display: inline-block;
    border: 3px solid rgba(255, 255, 255, .4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: install-spin .75s linear infinite;
}

.install-button__spinner {
    width: 18px;
    height: 18px;
}

.install-state-spinner {
    width: 28px;
    height: 28px;
}

.install-steps {
    margin: 4px 0 12px;
    padding: 0;
    list-style: none;
    counter-reset: install-step;
}

.install-steps li {
    position: relative;
    min-height: 40px;
    padding: 9px 6px 9px 50px;
    color: #4b5963;
    font-size: 15px;
    line-height: 1.45;
    counter-increment: install-step;
}

.install-steps li::before {
    content: counter(install-step);
    position: absolute;
    top: 5px;
    left: 0;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    color: #fff;
    background: #343a40;
    font-size: 14px;
    font-weight: 700;
}

.install-inline-icon {
    margin-left: 5px;
    color: #59636d;
}

.install-telegram-safari-preview {
    position: relative;
    width: min(100%, 330px);
    margin: 4px auto 15px;
    padding: 0;
    overflow: hidden;
    border: 1px solid rgba(34, 92, 165, .18);
    border-radius: 18px;
    background: #eaf3fc;
    box-shadow: 0 12px 30px rgba(31, 41, 51, .13);
    cursor: zoom-in;
}

.install-telegram-safari-preview img {
    display: block;
    width: 100%;
    height: auto;
}

.install-telegram-safari-preview__zoom {
    position: absolute;
    right: 10px;
    bottom: 10px;
    padding: 7px 10px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border-radius: 999px;
    color: #fff;
    background: rgba(7, 17, 31, .78);
    font-size: 12px;
    font-weight: 700;
    box-shadow: 0 5px 16px rgba(7, 17, 31, .22);
}

.install-telegram-safari-preview:focus-visible {
    outline: 3px solid #5da4ff;
    outline-offset: 3px;
}

.install-steps--telegram-ios {
    margin-top: 0;
}

.install-feedback,
.install-hint {
    margin: 15px 0 0 !important;
    font-size: 13px !important;
}

.install-feedback {
    color: #18753a !important;
    font-weight: 600;
}

.install-hint {
    color: #727f89 !important;
}

.install-footer {
    padding: 14px 24px 24px;
    color: #7a8791;
    font-size: 13px;
    text-align: center;
}

.install-preview-section {
    width: calc(100% - 48px);
    max-width: 1120px;
    margin: 22px auto 36px;
    padding: 28px;
    border: 1px solid rgba(134, 164, 201, .24);
    border-radius: 26px;
    background: rgba(255, 255, 255, .78);
    box-shadow: 0 18px 50px rgba(31, 68, 113, .08);
}

.install-preview-section__head {
    max-width: 760px;
    margin: 0 auto 22px;
    text-align: center;
}

.install-preview-section__head > span {
    color: #5277a6;
    font-size: 11px;
    letter-spacing: .14em;
    font-weight: 800;
}

.install-preview-section__head h2 {
    margin: 6px 0 7px;
    color: #143d72;
    font-size: clamp(24px, 3vw, 34px);
    line-height: 1.2;
}

.install-preview-section__head p {
    margin: 0;
    color: #65758a;
    font-size: 15px;
    line-height: 1.5;
}

@keyframes install-spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 820px) {
    .install-shell {
        max-width: 620px;
        padding-top: 28px;
        grid-template-columns: minmax(0, 1fr);
        gap: 30px;
    }

    .install-handoff {
        grid-template-columns: 48px minmax(0, 1fr);
    }

    .install-handoff__actions {
        grid-column: 1 / -1;
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .install-hero {
        text-align: center;
    }

    .install-brand {
        justify-content: center;
        margin-bottom: 24px;
    }

    .install-hero__lead {
        margin-right: auto;
        margin-left: auto;
    }

    .install-benefits {
        max-width: 360px;
        margin: 0 auto;
        text-align: left;
    }

    .install-card {
        min-height: 0;
        transform: none;
    }
}

@media (max-width: 480px) {
    .install-shell {
        padding: 20px 14px 14px;
        gap: 24px;
    }

    .install-handoff {
        grid-template-columns: 42px minmax(0, 1fr);
        padding: 12px;
    }

    .install-handoff__icon {
        width: 42px;
        height: 42px;
        border-radius: 12px;
    }

    .install-handoff__actions {
        grid-template-columns: 1fr;
    }

    .install-handoff__button {
        width: 100%;
        min-height: 44px;
        white-space: normal;
    }

    .install-preview-section {
        width: calc(100% - 28px);
        margin-top: 14px;
        margin-bottom: 24px;
        padding: 18px 13px;
        border-radius: 20px;
    }

    .install-brand__logo {
        width: 72px;
        height: 72px;
        border-radius: 20px;
    }

    .install-brand__name {
        font-size: 25px;
    }

    .install-brand__eyebrow {
        font-size: 11px;
    }

    .install-hero h1 {
        font-size: 37px;
    }

    .install-hero__lead {
        margin-top: 14px;
        margin-bottom: 24px;
        font-size: 17px;
    }

    .install-card {
        padding: 28px 20px;
        border-radius: 23px;
    }

    .install-card h2 {
        font-size: 22px;
    }

    .install-button {
        min-height: 52px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .install-button {
        transition: none;
    }

    .install-button:hover:not(:disabled) {
        transform: none;
    }
}
</style>
